import React from "react";
import { Trash2 } from "lucide-react";
import ConfirmDialog from "@/components/assistant/ConfirmDialog";

export type ConversationListItem = {
  conversation_id: string;
  title: string;
  updated_at_ms: number;
};

interface ConversationsPopupProps {
  conversations: ConversationListItem[];
  activeConversationId?: string | undefined;
  onSelectConversation: (conversationId: string) => void;
  loading?: boolean;
  streamingById?: Record<string, boolean>;
  onDeleteConversation?: (conversationId: string) => void;
}

const ConversationsPopup: React.FC<ConversationsPopupProps> = ({
  conversations,
  activeConversationId,
  onSelectConversation,
  loading,
  streamingById = {},
  onDeleteConversation,
}) => {
  const [pendingDeleteIds, setPendingDeleteIds] = React.useState<Record<string, boolean>>({});
  const [confirmConversationId, setConfirmConversationId] = React.useState<string | null>(null);
  type RecencyLabel = "Today" | "Yesterday" | "This Week" | "Earlier";
  const grouped = React.useMemo<Record<RecencyLabel, ConversationListItem[]>>(() => {
    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
    const startOfYesterday = startOfToday - 24 * 60 * 60 * 1000;
    const day = now.getDay();
    const diffToMonday = day === 0 ? 6 : day - 1;
    const startOfWeek = new Date(now.getFullYear(), now.getMonth(), now.getDate() - diffToMonday).setHours(0, 0, 0, 0);

    const buckets: Record<RecencyLabel, ConversationListItem[]> = {
      Today: [],
      Yesterday: [],
      "This Week": [],
      Earlier: [],
    };
    for (const c of conversations) {
      const t = c.updated_at_ms;
      if (t >= startOfToday) buckets.Today.push(c);
      else if (t >= startOfYesterday) buckets.Yesterday.push(c);
      else if (t >= startOfWeek) buckets["This Week"].push(c);
      else buckets.Earlier.push(c);
    }
    return buckets;
  }, [conversations]);

  const orderedGroupLabels = React.useMemo<RecencyLabel[]>(() => ["Today", "Yesterday", "This Week", "Earlier"], []);

  return (
    <div className="w-full">
      {loading ? (
        <div className="px-3 py-4 text-sm text-white/70 flex items-center gap-2">
          <span className="loading loading-spinner loading-xs" />
          <span>Loading…</span>
        </div>
      ) : (
        <div className="max-h-[60vh] overflow-y-auto pr-1 relative">
          {orderedGroupLabels.map((label) => {
            const items = grouped[label] || [];
            if (items.length === 0) return null;
            return (
              <div key={label} className="mb-3">
                <div className="px-2 py-1 text-[10px] uppercase tracking-wide text-white/40">{label}</div>
                <div className="mt-1 space-y-1.5">
                  {items.map((c) => {
                    const isStreaming = !!streamingById[c.conversation_id];
                    const isActive = activeConversationId === c.conversation_id;
                    if (pendingDeleteIds[c.conversation_id]) return null;
                    return (
                      <div key={c.conversation_id} className={`group relative rounded-lg transition-colors ${isActive ? "bg-white/10" : "hover:bg-white/5"}`}>
                        <div className={`absolute inset-y-0 left-0 w-0.5 rounded-l ${isActive ? 'bg-indigo-400/80' : 'bg-transparent group-hover:bg-white/20'}`} />
                        <button onClick={() => onSelectConversation(c.conversation_id)} className="w-full text-left pl-3 pr-10 py-2">
                          <div className="flex items-center justify-between gap-2">
                            <p className={`text-sm line-clamp-2 break-words ${isActive ? 'text-white font-medium' : 'text-white/70 group-hover:text-white'}`}>{c.title || c.conversation_id}</p>
                            {isStreaming && (
                              <div className="absolute top-1.5 right-1.5 pointer-events-none">
                                <div className="btn btn-ghost btn-xs p-0 w-6 h-6 flex items-center justify-center">
                                  <span className="loading loading-spinner loading-xs" />
                                </div>
                              </div>
                            )}
                          </div>
                          <p className={`text-[10px] ${isActive ? 'text-white/80' : 'text-white/50 group-hover:text-white/70'}`}>{new Date(c.updated_at_ms).toLocaleString()}</p>
                        </button>
                        {onDeleteConversation && !isStreaming && (
                          <button
                            type="button"
                            className="absolute top-1.5 right-1.5 opacity-0 group-hover:opacity-100 transition-opacity btn btn-ghost btn-xs p-1"
                            onClick={(e) => { e.stopPropagation(); setConfirmConversationId(c.conversation_id); }}
                            aria-label="Delete conversation"
                            title="Delete conversation"
                          >
                            <Trash2 className="h-4 w-4 text-error" />
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
          {conversations.length === 0 && (
            <div className="px-3 py-6 text-xs text-white/50">No conversations yet.</div>
          )}

          <ConfirmDialog
            open={!!confirmConversationId}
            title="Delete conversation?"
            description="This action cannot be undone."
            onCancel={() => setConfirmConversationId(null)}
            onConfirm={() => {
              const id = confirmConversationId as string;
              setConfirmConversationId(null);
              setPendingDeleteIds((s) => ({ ...s, [id]: true }));
              try {
                const maybe = onDeleteConversation?.(id) as void | Promise<void> | undefined;
                if (maybe && typeof (maybe as Promise<void>).then === "function") {
                  (maybe as Promise<void>).catch(() => {
                    setPendingDeleteIds((s) => { const n = { ...s }; delete n[id]; return n; });
                  });
                }
              } catch {
                setPendingDeleteIds((s) => { const n = { ...s }; delete n[id]; return n; });
              }
            }}
          />
        </div>
      )}
    </div>
  );
};

export default ConversationsPopup;


